import {
  MaliciousScoreFactorsChart,
  ProfileCategoriesBarChart,
  QLink,
  RegulationsTable,
  RulesTable,
  Sbom,
  ThreatTypeDistributionChartCard,
  LoadingReport,
  QButton,
  QTypography,
  AppMetadataPanelContent,
  MaliciousScoreChart,
  NetworkMap,
  NetworkTrafficTable,
  transformNetworkOverviewData,
  NetworkOverviewTable,
} from '@kw/quokka-ui';

import fileSize from 'filesize';
import { useReactToPrint } from 'react-to-print';
import { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import useSWR from 'swr';
import { useLocation, useParams } from 'react-router-dom';
import { GuardianReportHeaderAndMetadata } from '../GuardianReportHeaderAndMetadata';
import { PermissionsChart } from '../charts/PermissionsChart';
import { RulesChart } from '../charts/RulesChart';
import { getMaliciousThreatLevel, getMaliciousThreatTypes, processRules, sortRegulation } from '../utils/dopplerUtil';
import { fetchService } from '../../../../utils/fetchService';
import { useLocalStorage } from '../../../../utils/useLocalStorage';
import { BehaviorsChartCard } from '../charts/BehaviorsChart';
import { AppAnalysisType, AppDetailsType } from '../types/ApplicationDetailsType';
import { buildCustomRulesObject, getCategoryFromRuleId, isBehavioral } from '../utils/helper';
import { NavigationContext } from '../../../Navigation/NavigationProvider';
import { useOrganization } from '../../../../utils/useOrganization';
import { useHighRiskCountries } from '../../hooks';

import './custom.css';

export function NewReportPdfView() {
  const NUM_OF_EVIDENCES_PER_RULE = 10;

  const location = useLocation();

  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { orgData } = useOrganization(accessToken);
  const [isLoading, setIsLoading] = useState(true);
  const [aggregateData, setAggregateData] = useState<any>({});
  const [ruleSummaries, setRuleSummaries] = useState([]);
  const [nonBehavioralRuleSummaries, setNonBehavioralRuleSummaries] = useState([]);
  const [behavioralRuleSummaries, setBehavioralRuleSummaries] = useState([]);
  const [maliciousRuleSummaries, setMaliciousRuleSummaries] = useState([]);
  const [threatScore, setThreatScore] = useState();
  const { analysisId } = useParams();

  const { data: reportLoaded } = useSWR(`/eval/results/${analysisId}/is-data-loaded`, path => fetchService(path, accessToken), {
    suspense: true,
  });

  const { highRiskCountries = [] } = useHighRiskCountries(accessToken);

  const [appId, setAppId] = useState('');
  const [app, setApp] = useState();
  const [metadata, setMetadata] = useState({});
  const [maliciousResult, setMaliciousResult] = useState({});
  const [sbomData, setSbomData] = useState([]);
  const [sslPinningData, setSslPinningData] = useState({} as any);
  const [networkTrafficData, setNetworkTrafficData] = useState({} as any);
  const [geolocationsData, setGeolocationsData] = useState([]);
  const [staticUrisData, setStaticUrisData] = useState({} as any);
  const [networkOverviewData, setNetworkOverviewData] = useState({} as any);
  const [pageTitle, setPageTitle] = useState('');

  const { setAppName: setAppTitle } = useContext(NavigationContext);

  const sbomAccess = true;
  const maliciousAccess = true;
  const niapAccess = true;
  const gdprAccess = true;
  const owaspAccess = true;
  const iosSbom = true;

  const [appDetails, setAppDetails] = useState<AppDetailsType>();
  const [appAnalysis, setAppAnalysis] = useState<AppAnalysisType>();
  const [appName, setAppName] = useState('');

  const [appVersion, setAppVersion] = useState('');
  const [appPlatform, setAppPlatform] = useState('');
  const [appInfo, setAppInfo] = useState({});
  const [marketInfo, setMarketInfo] = useState({});

  const [foreignConnections, setForeignConnections] = useState(null);
  const [customRules, setCustomRules] = useState({});

  // Determine if SBOM tab should be shown based on platform and feature flags
  const shouldShowSbomTab = sbomAccess && (appPlatform !== 'ios' || iosSbom);

  const [bulkEvidences, setBulkEvidences] = useState({});
  const [masvsData, setMasvsData] = useState({
    fail: null,
    review: null,
  });
  const [owasp2024Data, setOwasp2024Data] = useState({
    fail: null,
    review: null,
  });
  const [owasp2016Data, setOwasp2016Data] = useState({
    fail: null,
    review: null,
  });
  const [gdprData, setGdprData] = useState({
    fail: null,
    review: null,
  });
  const [niapData, setNiapData] = useState({
    fail: null,
    review: null,
  });
  // eslint-disable-next-line camelcase
  const [niap1_3Data, setNiap1_3Data] = useState({
    fail: null,
    review: null,
  });
  // eslint-disable-next-line camelcase
  const [niap1_4Data, setNiap1_4Data] = useState({
    fail: null,
    review: null,
  });
  // eslint-disable-next-line camelcase
  const [niap2_0Data, setNiap2_0Data] = useState({
    fail: null,
    review: null,
  });

  const [maliciousScore, setMaliciousScore] = useState(null);
  const [maliciousThreatTypes, setMaliciousThreatTypes] = useState(null);

  const [riskOptions, setRiskOptions] = useState([]);
  const [tabOptions, setTabOptions] = useState([]);
  const [evidenceOption, setEvidenceOption] = useState('');
  const [ruleCategories, setRuleCategories] = useState([]);
  const [overviewRuleCategories, setOverviewRuleCategories] = useState([]);
  const [behavioralRuleCategories, setBehavioralRuleCategories] = useState([]);
  const [showManuallyPassedItems, setShowManuallyPassedItems] = useState<boolean>();

  const getApp = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/applications/appDetails/${analysisId}`, accessToken);

      const { appAnalysis, appDetails } = response;
      setAppAnalysis(appAnalysis);
      setAppDetails(appDetails);

      setAppName(appDetails?.appName);
      setAppTitle(appDetails?.appName);
      setAppId(appDetails?.appId);
      setAppVersion(appDetails?.version);
      setAppPlatform(appDetails?.os);

      setAppInfo({
        name: appDetails?.appName,
        platform: appDetails?.os,
        version: appDetails?.version,
        versionCode: appDetails?.versionCode,
        minSdk: appDetails?.minOsVersion,
        targetSdk: null,
        fileSize: appDetails?.size ? fileSize(appDetails?.size) : '',
        submissionDate: appAnalysis?.dateSubmitted,
        submissionTag: `App Type - ${_.capitalize(appAnalysis?.management)}`,
        sha1: null,
        md5: null,
        substitutedVersion: appDetails?.analyzedVersion,
      });

      setMarketInfo({
        category: appDetails?.categories?.map(category => _.capitalize(category)).join(', ') || '',
        storeUrl: appDetails?.storeUrl,
        developer: appDetails?.developerName,
        developerUrl: appDetails?.developerUrl,
        privacyPolicy: appDetails?.privacyPolicyUrl,
      });
    } catch (err) {
      console.error('Error loading app data: ', err);
    }
  }, [analysisId, accessToken]);

  const fetchAggregateData = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/aggregate-data`, accessToken);
      setAggregateData(response);
    } catch (err) {
      console.error('Error loading aggregate data: ', err);
      setAggregateData({});
    }
  }, [analysisId, accessToken]);

  // Only called inside fetchRuleSummaries after the data is available
  const fetchMaliciousResult = async ruleSummaries => {
    try {
      const malwareRule = ruleSummaries?.find(ruleSummary => ruleSummary.rule?.id?.startsWith('malware/'));
      if (malwareRule) {
        const response = await fetchService(`/eval/results/${analysisId}/evidence?ruleGuid=${malwareRule?.guid}`, accessToken);
        const result = response.entries && response.entries.length > 0 ? response.entries[0].result : {};
        setMaliciousResult(result);
        setMaliciousScore(getMaliciousThreatLevel(result, true));
        setMaliciousThreatTypes(getMaliciousThreatTypes(result, true));
      } else {
        setMaliciousResult({});
      }
    } catch (err) {
      console.error('Error loading rule summary data: ', err);
      setMaliciousResult({});
    }
  };

  const fetchRuleSummaries = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/rule-summaries`, accessToken);
      const fullRuleSummaries = processRules(response);
      setRuleSummaries(fullRuleSummaries);

      const requestedRiskLevelRuleSummaries = fullRuleSummaries.filter(ruleSummary =>
        riskOptions.includes(ruleSummary.result_level_numeric),
      );

      const validateCategory = (ruleSummary, categories) => {
        const ruleCategory = getCategoryFromRuleId(ruleSummary.rule?.id);
        return categories.length > 0 && ruleCategory ? categories.includes(ruleCategory) : true;
      };

      const behavioralFilteredData = requestedRiskLevelRuleSummaries.filter(
        ruleSummary => isBehavioral(ruleSummary) && validateCategory(ruleSummary, behavioralRuleCategories),
      );

      setNonBehavioralRuleSummaries(
        requestedRiskLevelRuleSummaries.filter(
          ruleSummary => ruleSummary.details?.category !== 'behavioral' && validateCategory(ruleSummary, overviewRuleCategories),
        ),
      );
      setBehavioralRuleSummaries(behavioralFilteredData);
      setMaliciousRuleSummaries(behavioralFilteredData);

      if (maliciousAccess) {
        await fetchMaliciousResult(requestedRiskLevelRuleSummaries);
      }
    } catch (err) {
      console.error('Error loading rule summary data: ', err);
      setRuleSummaries([]);
      setNonBehavioralRuleSummaries([]);
      setBehavioralRuleSummaries([]);
    }
  }, [analysisId, accessToken, riskOptions]);

  const fetchBulkEvidences = useCallback(async () => {
    try {
      const response = await fetchService(
        `/eval/results/${analysisId}/bulk-evidences?uuid=${analysisId}&numOfEvidences=${NUM_OF_EVIDENCES_PER_RULE}`,
        accessToken,
      );
      setBulkEvidences(response);
    } catch (err) {
      console.error('Error loading rule summary data: ', err);
      setBulkEvidences(null);
    }
  }, [analysisId, accessToken]);

  const fetchThreatScore = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/threat-score`, accessToken);
      setThreatScore(response.threatScore);
    } catch (err) {
      console.error('Error loading threat score ', err);
    }
  }, [analysisId, accessToken]);

  // const fetchMetadata = useCallback(async () => {
  // }, [analysisId, accessToken]);

  const fetchSbomData = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/sbom`, accessToken);

      setSbomData(response);
    } catch (err) {
      console.error('Error loading SBOM data: ', err);
    }
  }, [analysisId, accessToken]);

  const fetchNetworkTrafficData = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/traffic`, accessToken);

      setNetworkTrafficData(response);
    } catch (err) {
      console.error('Error loading traffic data: ', err);
    }
  }, [analysisId, accessToken]);

  const fetchGeolocationsData = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/geolocation`, accessToken);

      setGeolocationsData(response);
    } catch (err) {
      console.error('Error loading geolocations data: ', err);
    }
  }, [analysisId, accessToken]);

  const fetchSslPinningData = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/ssl-pinning`, accessToken);

      setSslPinningData(response);
    } catch (err) {
      console.error('Error loading ssl pinning data: ', err);
    }
  }, [analysisId, accessToken]);

  const fetchStaticUrisData = useCallback(async () => {
    try {
      const response = await fetchService(`/eval/results/${analysisId}/static-uris`, accessToken);

      setStaticUrisData(response);
    } catch (err) {
      console.error('Error loading static URIs data: ', err);
    }
  }, [analysisId, accessToken]);

  const fetchRegulationData = useCallback(
    async (regulationType, setDataFunction) => {
      try {
        const response = await fetchService(`/eval/results/${analysisId}/regulations?type=${regulationType}`, accessToken);
        for (const key of Object.keys(response.fail)) {
          response.fail[key] = processRules(response.fail[key]);
        }
        for (const key2 of Object.keys(response.review)) {
          response.review[key2] = processRules(response.review[key2]);
        }
        setDataFunction({
          fail: sortRegulation(response.fail),
          review: sortRegulation(response.review),
        });
      } catch (err) {
        console.log(`Error loading ${regulationType} regulation data: `, err);
        setDataFunction({ fail: null, review: null });
      }
    },
    [analysisId, accessToken],
  );

  // ------- CUSTOM RULES -------
  // fetching foreign connections data separately since it's only needed for overview tab

  const fetchForeignConnections = useCallback(
    async homeCountryCode => {
      const response = await fetchService(
        `/eval/results/${analysisId}/foreign-connections?homeCountryCode=${homeCountryCode}`,
        accessToken,
      );
      setForeignConnections(response);
    },
    [accessToken, analysisId],
  );

  useEffect(() => {
    if (!tabOptions.includes('overview')) return; // only fetch foreign connections data if overview tab is included in the report
    if (!orgData?.homeCountryCode) return;

    fetchForeignConnections(orgData?.homeCountryCode).catch(err => {
      console.error('Error loading foreign connections data: ', err);
    });
  }, [orgData?.homeCountryCode, tabOptions]);

  useEffect(() => {
    if (!foreignConnections) return;

    const customRules = buildCustomRulesObject({ possibleForeignConnectionRule: foreignConnections });
    setCustomRules(customRules);
  }, [foreignConnections]);

  // ------- END OF CUSTOM RULES -------

  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({
    documentTitle: `${appId}.${appVersion}`,
    contentRef,
  });

  const extractParams = (queryParams, paramList) =>
    paramList.reduce((acc, key) => {
      acc[key] = queryParams.get(key);
      return acc;
    }, {});

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const paramList = ['risks', 'tabs', 'evidence', 'overview_rule_categories', 'behavioral_rule_categories', 'show_manually_passed_items'];
    const params = extractParams(queryParams, paramList);
    const overviewCategories = params?.overview_rule_categories?.split(',');
    const behaviorsCategories = params?.behavioral_rule_categories?.split(',');

    setRiskOptions(params?.risks?.split(',').map(Number));
    setTabOptions(params?.tabs?.split(','));
    setEvidenceOption(params.evidence);
    setOverviewRuleCategories(overviewCategories);
    setBehavioralRuleCategories(behaviorsCategories);
    // For fetching bulk evidences to be passed for all rules tables
    setRuleCategories(overviewCategories?.concat(behaviorsCategories));
    setShowManuallyPassedItems(params?.show_manually_passed_items !== 'false');
  }, []);

  useEffect(() => {
    const loadDataConcurrently = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchAggregateData(),
          getApp(),
          (tabOptions.includes('overview') || tabOptions.includes('behavior') || tabOptions.includes('malicious_report')) &&
            (overviewRuleCategories.length > 0 || behavioralRuleCategories.length > 0) &&
            fetchRuleSummaries(),
          evidenceOption !== 'none' && ruleCategories.length > 0 && fetchBulkEvidences(),
          fetchThreatScore(),
          shouldShowSbomTab && tabOptions.includes('sbom') && fetchSbomData(),
          tabOptions.includes('network_info') && fetchGeolocationsData(),
          tabOptions.includes('network_info') && fetchNetworkTrafficData(),
          tabOptions.includes('network_info') && fetchStaticUrisData(),
          tabOptions.includes('network_info') && fetchSslPinningData(),
          owaspAccess && tabOptions.includes('owasp_masvs') && fetchRegulationData('masvs', setMasvsData),
          owaspAccess && tabOptions.includes('owasp_2024') && fetchRegulationData('owasp-2024', setOwasp2024Data),
          owaspAccess && tabOptions.includes('owasp_2016') && fetchRegulationData('owasp-2016', setOwasp2016Data),
          gdprAccess && tabOptions.includes('gdpr') && fetchRegulationData('gdpr', setGdprData),
          niapAccess && tabOptions.includes('niap-1.3') && fetchRegulationData('niap-1.3', setNiap1_3Data),
          niapAccess && tabOptions.includes('niap-1.4') && fetchRegulationData('niap-1.4', setNiap1_4Data),
          niapAccess && tabOptions.includes('niap-2.0') && fetchRegulationData('niap-2.0', setNiap2_0Data),
        ]);
      } catch (error) {
        console.error('Error loading data: ', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDataConcurrently();
  }, [tabOptions, evidenceOption, ruleCategories, overviewRuleCategories, behavioralRuleCategories]);

  useEffect(() => {
    const title = `${appName ?? ''} PDF Report`;
    setPageTitle(title);
  }, [appName]);

  const PdfOnlyLink = useCallback(
    ({ href, children }) => (
      <QLink
        href={href}
        style={{
          pointerEvents: 'none',
          fontSize: '16px',
        }}
        className="pdf-only-link"
      >
        {children}
      </QLink>
    ),
    [],
  );

  const getOverviewTabCharts = (ruleSummaries, aggregateData, maliciousResult) => {
    const securityOrPrivacyRuleSummaries = (ruleSummaries || [])?.filter(
      ruleSummary => ruleSummary.rule?.properties?.category === 'security' || ruleSummary.rule?.properties?.category === 'privacy',
    );

    const computeLevelSummary = ruleSummary => {
      const levels = ['none', 'note', 'warning', 'error'];
      // Count the occurrences of each level. '0' is 'none', '1' is 'note', '2' is 'warning', and '3' is 'error'.
      const levelCounts = ruleSummary?.reduce(
        (acc, rule) => {
          const levelKey = levels[rule.result_level_numeric] ?? 'none';
          acc[levelKey] = (acc[levelKey] || 0) + 1;
          return acc;
        },
        { none: 0, note: 0, warning: 0, error: 0 },
      );

      return levels.map(level => ({
        level,
        resultsCount: (levelCounts?.[level] ?? 0).toString(),
      }));
    };

    const levelSummary = computeLevelSummary(securityOrPrivacyRuleSummaries);

    const charts = [];

    if (levelSummary) {
      charts.push(<RulesChart rulesSummary={levelSummary} />);
    }

    if (aggregateData?.permissionSummary) {
      charts.push(<PermissionsChart permissionsSummary={aggregateData.permissionSummary} />);
    }

    if (!_.isEmpty(maliciousResult)) {
      const maliciousScore = getMaliciousThreatLevel(maliciousResult);
      charts.push(<MaliciousScoreChart score={maliciousScore} />);
    }

    return charts;
  };

  const overviewTabCharts = useMemo(
    () => getOverviewTabCharts(ruleSummaries, aggregateData, maliciousResult),
    [ruleSummaries, aggregateData, maliciousResult],
  );

  const maliciousDetails = useMemo(() => maliciousRuleSummaries.map(behavior => behavior.details), [maliciousRuleSummaries]);

  useEffect(() => {
    fetchMaliciousResult(ruleSummaries);
  }, [ruleSummaries]);

  useEffect(() => {
    setNetworkOverviewData(
      transformNetworkOverviewData({
        sslPinning: sslPinningData?.tlsConnections ?? [],
        staticUrls: staticUrisData?.staticUris ?? [],
        geolocations: geolocationsData,
      }),
    );
  }, [sslPinningData, staticUrisData, geolocationsData]);

  const printHeaderStyles = useMemo(
    () => `
      @page {
          margin-top: 10mm;
          margin-bottom: 10mm;
          margin-left: 5mm;
          margin-right: 5mm;
          size: A4;
          @top-left {
              content: "Powered by Quokka";
              font-size: 6pt;
              font-family: "Nunito Sans", NunitoSans, sans-serif;
              color: #757575;
              width: 30%;
          }
          @top-right {
              content: "${appPlatform} - ${appName} - ${appId} - ${appVersion}";
              font-size: 6pt;
              font-family: "Nunito Sans", NunitoSans, sans-serif;
              color: #757575;
              width: 70%;
          }
          @bottom-left {
              content: "All rights reserved. No part of this document may be reproduced or transmitted in any form or by any means, electronic, mechanical, photocopying, recording, or otherwise, without prior written permission of Quokka";
              font-size: 6pt;
              font-family: "Nunito Sans", NunitoSans, sans-serif;
              color: #757575;
              width: 70%;
          }
          @bottom-right {
              content: "Page " counter(page) " of " counter(pages);
              font-size: 6pt;
              font-family: "Nunito Sans", NunitoSans, sans-serif;
              color: #757575;
              width: 30%;
          }
      }
    `,
    [appName, appId, appPlatform, appVersion],
  );

  if (isLoading) {
    return (
      <div
        style={{
          height: '500px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingReport />
      </div>
    );
  }

  return (
    <Suspense fallback={<div>Generating PDF Report...</div>}>
      <style>{printHeaderStyles}</style>
      <div className="pdf-flex-column-container" style={{ alignItems: 'center' }}>
        <div className="pdf-flex-row-container" style={{ gap: '10px', width: '210mm' }}>
          <QButton onClick={reactToPrintFn} variant="filled">
            Print
          </QButton>
        </div>
        <div className="pdf-printable-container" ref={contentRef}>
          <div className="pdf-flex-column-container" style={{ gap: '0px' }}>
            <img src="/img/Quokka_Banner_V2.svg" alt="Quokka Banner" style={{ height: '60px', width: 'auto' }} />
            <GuardianReportHeaderAndMetadata
              appName={appName}
              appId={appId}
              appVersion={appVersion}
              os={appPlatform}
              iconUrl={appDetails?.appIconBase64 ? `data:image/png;base64,${appDetails.appIconBase64}` : ''}
              appDetails={appDetails}
              appAnalysis={appAnalysis}
              tag={undefined}
              threatScore={threatScore}
            />
          </div>
          <div className="pdf-flex-column-container">
            <div className="pdf-section-title" style={{ marginBottom: '0px' }}>
              <QTypography variant="h4Medium">Overview</QTypography>
            </div>
            <div
              className="pdf-flex-row-container"
              style={{
                flexWrap: 'wrap',
              }}
            >
              {overviewTabCharts.map((chart, index) =>
                index === 2 ? (
                  <div style={{ width: '50mm', height: '60mm' }}>{chart}</div>
                ) : (
                  <div style={{ width: '75mm', height: '60mm' }}>{chart}</div>
                ),
              )}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                pageBreakBefore: 'avoid',
              }}
            >
              <AppMetadataPanelContent appInfo={appInfo} marketInfo={marketInfo} pdfFriendly />
            </div>
          </div>

          <div className="pdf-flex-column-container">
            <div className="pdf-section-title" style={{ pageBreakBefore: 'always' }}>
              <QTypography variant="h4Medium">Table of Contents</QTypography>
            </div>
            <div className="pdf-flex-column-container" style={{ marginLeft: '20px' }}>
              <div className="pdf-flex-row-container">
                <div className="pdf-flex-column-container" style={{ flex: 1 }}>
                  {nonBehavioralRuleSummaries.length > 0 && bulkEvidences && tabOptions.includes('overview') && (
                    <PdfOnlyLink href="#overview-findings">Overview Findings</PdfOnlyLink>
                  )}
                  {behavioralRuleSummaries.length > 0 && bulkEvidences && tabOptions.includes('behavior') && (
                    <PdfOnlyLink href="#behavioral-findings">Behavioral Findings</PdfOnlyLink>
                  )}
                  {geolocationsData.length > 0 && tabOptions.includes('network_info') && (
                    <PdfOnlyLink href="#network-map">Network Map</PdfOnlyLink>
                  )}
                  {networkOverviewData.length && tabOptions.includes('network_info') && (
                    <PdfOnlyLink href="#network-overview">Network Overview Table</PdfOnlyLink>
                  )}
                  {networkTrafficData?.networkTraffic && tabOptions.includes('network_info') && (
                    <PdfOnlyLink href="#network-traffic">Network Traffic Table</PdfOnlyLink>
                  )}
                  {sbomData.length > 0 && shouldShowSbomTab && tabOptions.includes('sbom') && <PdfOnlyLink href="#sbom">SBOM</PdfOnlyLink>}
                  {Object.keys(maliciousResult).length > 0 && tabOptions.includes('malicious_report') && (
                    <PdfOnlyLink href="#malicious-report">Malicious Report</PdfOnlyLink>
                  )}
                </div>
                <div className="pdf-flex-column-container" style={{ flex: 1 }}>
                  {masvsData.fail && masvsData.review && tabOptions.includes('owasp_masvs') && (
                    <PdfOnlyLink href="#masvs">OWASP MASVS</PdfOnlyLink>
                  )}
                  {owasp2024Data.fail && owasp2024Data.review && tabOptions.includes('owasp_2024') && (
                    <PdfOnlyLink href="#owasp-2024">OWASP Mobile Top 10 2024</PdfOnlyLink>
                  )}
                  {owasp2016Data.fail && owasp2016Data.review && tabOptions.includes('owasp_2016') && (
                    <PdfOnlyLink href="#owasp-2016">OWASP Mobile Top 10 2016</PdfOnlyLink>
                  )}
                  {gdprData.fail && gdprData.review && tabOptions.includes('gdpr') && <PdfOnlyLink href="#gdpr">GDPR</PdfOnlyLink>}
                  {niap1_3Data.fail && niap1_3Data.review && tabOptions.includes('niap-1.3') && (
                    <PdfOnlyLink href="#niap-1.3">NIAP 1.3</PdfOnlyLink>
                  )}
                  {niap1_4Data.fail && niap1_4Data.review && tabOptions.includes('niap-1.4') && (
                    <PdfOnlyLink href="#niap-1.4">NIAP 1.4</PdfOnlyLink>
                  )}
                  {niap2_0Data.fail && niap2_0Data.review && tabOptions.includes('niap-2.0') && (
                    <PdfOnlyLink href="#niap-2.0">NIAP 2.0</PdfOnlyLink>
                  )}
                </div>
              </div>
            </div>
          </div>

          {nonBehavioralRuleSummaries.length > 0 && bulkEvidences && tabOptions.includes('overview') && (
            <div
              style={{
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
              }}
            >
              <div className="pdf-section-title" id="overview-findings" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">Overview Findings</QTypography>
              </div>
              <RulesTable
                ruleSummaries={nonBehavioralRuleSummaries}
                uuid={analysisId}
                bulkEvidences={bulkEvidences}
                pdfFriendly
                showManuallyPassedItems={showManuallyPassedItems}
                customRules={customRules}
              />
            </div>
          )}

          {behavioralRuleSummaries.length > 0 && bulkEvidences && tabOptions.includes('behavior') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="behavioral-findings" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">Behavioral Findings</QTypography>
              </div>
              <RulesTable
                ruleSummaries={behavioralRuleSummaries}
                uuid={analysisId}
                bulkEvidences={bulkEvidences}
                pdfFriendly
                showManuallyPassedItems={showManuallyPassedItems}
              />
            </div>
          )}

          {geolocationsData.length > 0 && tabOptions.includes('network_info') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="section-title" id="network-map" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">Network Map</QTypography>
              </div>
              <NetworkMap geolocations={geolocationsData} mapTilerApiKey={process.env.REACT_APP_MAPTILER_API_KEY} pdfFriendly />
            </div>
          )}

          {networkOverviewData.length > 0 && tabOptions.includes('network_info') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="section-title" id="network-overview" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">Network Overview</QTypography>
              </div>
              <NetworkOverviewTable traffic={networkOverviewData} highRiskCountries={highRiskCountries} pdfFriendly />
            </div>
          )}

          {networkTrafficData?.networkTraffic && tabOptions.includes('network_info') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="section-title" id="network-traffic" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">Network Traffic Table</QTypography>
              </div>
              <NetworkTrafficTable traffic={networkTrafficData.networkTraffic} uuid={analysisId} pdfFriendly />
            </div>
          )}

          {sbomData.length > 0 && shouldShowSbomTab && tabOptions.includes('sbom') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="sbom" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">SBOM</QTypography>
              </div>
              <Sbom data={sbomData} pdfFriendly />
            </div>
          )}

          {Object.keys(maliciousResult).length > 0 && tabOptions.includes('malicious_report') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="malicious-report" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">Malicious Report</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <div className="pdf-flex-row-container">
                  {maliciousScore && (
                    <div style={{ flex: 1 }}>
                      <MaliciousScoreFactorsChart score={maliciousScore} threatTypes={maliciousThreatTypes} />
                    </div>
                  )}
                  {maliciousThreatTypes && (
                    <div style={{ flex: 1 }}>
                      <ThreatTypeDistributionChartCard threatTypes={maliciousThreatTypes} />
                    </div>
                  )}
                </div>
                <div className="pdf-flex-row-container">
                  <div style={{ flex: 1 }}>
                    <PermissionsChart permissionsSummary={aggregateData?.permissionSummary} />
                  </div>

                  <div style={{ flex: 1 }}>
                    <BehaviorsChartCard results={maliciousDetails} />
                  </div>
                </div>
                <div className="pdf-flex-row-container">
                  <div style={{ flex: 1, pageBreakBefore: 'always' }}>
                    <ProfileCategoriesBarChart results={maliciousDetails} />
                  </div>
                </div>
                <div>
                  <RulesTable
                    ruleSummaries={maliciousRuleSummaries}
                    uuid={analysisId}
                    pdfFriendly
                    bulkEvidences={bulkEvidences}
                    showManuallyPassedItems={showManuallyPassedItems}
                  />
                </div>
              </div>
            </div>
          )}

          {masvsData.fail && masvsData.review && tabOptions.includes('owasp_masvs') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="masvs" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">OWASP MASVS</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <RegulationsTable
                  initialRegulationOption="masvs"
                  regulation={masvsData}
                  uuid={analysisId}
                  pdfFriendly
                  bulkEvidences={bulkEvidences}
                  platform={_.lowerCase(appPlatform)}
                  showManuallyPassedItems={showManuallyPassedItems}
                />
              </div>
            </div>
          )}

          {owasp2024Data.fail && owasp2024Data.review && tabOptions.includes('owasp_2024') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="owasp-2024" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">OWASP Mobile Top 10 2024</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <RegulationsTable
                  initialRegulationOption="owasp-2024"
                  regulation={owasp2024Data}
                  uuid={analysisId}
                  pdfFriendly
                  bulkEvidences={bulkEvidences}
                  platform={_.lowerCase(appPlatform)}
                  showManuallyPassedItems={showManuallyPassedItems}
                />
              </div>
            </div>
          )}

          {owasp2016Data.fail && owasp2016Data.review && tabOptions.includes('owasp_2016') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="owasp-2016" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">OWASP Mobile Top 10 2016</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <RegulationsTable
                  initialRegulationOption="owasp-2016"
                  regulation={owasp2016Data}
                  uuid={analysisId}
                  pdfFriendly
                  bulkEvidences={bulkEvidences}
                  platform={_.lowerCase(appPlatform)}
                  showManuallyPassedItems={showManuallyPassedItems}
                />
              </div>
            </div>
          )}

          {gdprData.fail && gdprData.review && tabOptions.includes('gdpr') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="gdpr" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">GDPR</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <RegulationsTable
                  initialRegulationOption="gdpr"
                  regulation={gdprData}
                  uuid={analysisId}
                  pdfFriendly
                  bulkEvidences={bulkEvidences}
                  platform={_.lowerCase(appPlatform)}
                  showManuallyPassedItems={showManuallyPassedItems}
                />
              </div>
            </div>
          )}

          {niap1_3Data.fail && niap1_3Data.review && tabOptions.includes('niap-1.3') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="niap" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">NIAP 1.3</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <RegulationsTable
                  initialRegulationOption="niap-1.3"
                  // eslint-disable-next-line camelcase
                  regulation={niap1_3Data}
                  uuid={analysisId}
                  pdfFriendly
                  bulkEvidences={bulkEvidences}
                  platform={_.lowerCase(appPlatform)}
                  showManuallyPassedItems={showManuallyPassedItems}
                />
              </div>
            </div>
          )}
          {niap1_4Data.fail && niap1_4Data.review && tabOptions.includes('niap-1.4') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="niap" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">NIAP 1.4</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <RegulationsTable
                  initialRegulationOption="niap-1.4"
                  // eslint-disable-next-line camelcase
                  regulation={niap1_4Data}
                  uuid={analysisId}
                  pdfFriendly
                  bulkEvidences={bulkEvidences}
                  platform={_.lowerCase(appPlatform)}
                  showManuallyPassedItems={showManuallyPassedItems}
                />
              </div>
            </div>
          )}
          {niap2_0Data.fail && niap2_0Data.review && tabOptions.includes('niap-2.0') && (
            <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="pdf-section-title" id="niap" style={{ pageBreakBefore: 'always' }}>
                <QTypography variant="h4Medium">NIAP 2.0</QTypography>
              </div>
              <div className="pdf-flex-column-container">
                <RegulationsTable
                  initialRegulationOption="niap-2.0"
                  // eslint-disable-next-line camelcase
                  regulation={niap2_0Data}
                  uuid={analysisId}
                  pdfFriendly
                  bulkEvidences={bulkEvidences}
                  platform={_.lowerCase(appPlatform)}
                  showManuallyPassedItems={showManuallyPassedItems}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Suspense>
  );
}
